import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { roleAtLeast, type Role } from '../lib/auth.js';

/**
 * §95.4 — "Dışa aktar" düğmesi: iki maddelik menü (CSV · JSONL), üç sayfada aynı
 * bileşen (Oturumlar · Bulgular · Geri Dönüş).
 *
 * İNDİRME `<a href>` İLEDİR, fetch+blob DEĞİL (§95.4): büyük dosyada tarayıcının
 * kendi indirme yöneticisi çalışır, panel belleği şişmez. Oturum çerezi istekle
 * kendiliğinden gider. (Servis belirteciyle — ADMIN_TOKEN — gezen biri için bu
 * bağlantı 403 döner: belirteç başlıkta taşınır ve `<a>` başlık taşıyamaz; panel
 * kullanıcısının gerçek yolu çerezdir, kural budur.)
 *
 * `viewer` düğmeyi HİÇ görmez: export panel-dışına veri çıkarmadır ve yetki
 * `editor` ister (§91.2 KARAR 5) — karar sunucuda, görünürlük burada.
 *
 * ŞARTNAME DÜZELTMESİ (Faz 10 denetimi, bulgu B10) — §95.4 iki kuralı AYNI ANDA
 * istiyordu ve ikisi birlikte imkânsızdı: "indirme `<a download>` iledir" ve
 * "429'da hata şeridi gösterilir". Tarayıcı indirmesinin durum kodu JS'e DÖNMEZ;
 * gün tavanına takılan kullanıcı hiçbir şey göremiyor, boş/bozuk bir dosya iniyor
 * sanıyordu.
 *
 * Çözüm ikisini de korur: indirme yolu `<a>` olarak KALIR (akış yanıtını `fetch`
 * ile belleğe almak 100k satırlık dosyada panelin belleğini yerdi), tavan ise
 * indirme BAŞLAMADAN ÖNCE `HEAD` ile yoklanır. 429 gelirse indirme hiç başlamaz
 * ve şerit gösterilir; başka her durumda (ağ hatası, uç HEAD desteklemiyor)
 * indirme ENGELLENMEZ — yoklama bir kolaylıktır, kapı değildir.
 */
export default function DisaAktarDugmesi({
  pid,
  dataset,
  role,
  params = {},
}: {
  pid: string;
  dataset: 'sessions' | 'insights' | 'retention';
  role: Role | null;
  /** Sayfanın aktif filtreleri — "gördüğünü indir" (§95.1). */
  params?: Record<string, string | undefined>;
}) {
  const [acik, setAcik] = useState(false);
  const [tavanUyarisi, setTavanUyarisi] = useState<string | null>(null);
  const kok = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!acik) return;
    const dinle = (e: MouseEvent) => {
      if (kok.current !== null && !kok.current.contains(e.target as Node)) setAcik(false);
    };
    document.addEventListener('mousedown', dinle);
    return () => document.removeEventListener('mousedown', dinle);
  }, [acik]);

  if (!roleAtLeast(role, 'editor')) return null;

  function href(format: 'csv' | 'jsonl'): string {
    const arama = new URLSearchParams({ format });
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') arama.set(k, v);
    }
    return `/api/projects/${encodeURIComponent(pid)}/export/${dataset}?${arama.toString()}`;
  }

  /**
   * §95.4 — gün tavanı ÖN YOKLAMASI. Yalnız 429 indirmeyi durdurur; ağ hatası ya da
   * HEAD desteklemeyen bir uç kullanıcıyı ENGELLEMEZ (yoklama kapı değildir).
   */
  async function tavanaTakildiMi(url: string): Promise<boolean> {
    try {
      const res = await fetch(url, { method: 'HEAD', credentials: 'same-origin' });
      return res.status === 429;
    } catch {
      return false;
    }
  }

  return (
    <div ref={kok} className="relative" data-testid={`disa-aktar-${dataset}`}>
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] bg-white px-3 py-1.5 text-[12px] text-[#374151] transition-colors hover:border-[#c8c8c8]"
      >
        <Download className="h-3.5 w-3.5" /> Dışa aktar
        <ChevronDown className="h-3 w-3 text-[#9ca3af]" />
      </button>
      {acik ? (
        <div className="absolute right-0 top-full z-40 mt-1.5 w-40 rounded-lg border border-[#e5e7eb] bg-white py-1 shadow-lg">
          {(['csv', 'jsonl'] as const).map((format) => (
            <a
              key={format}
              href={href(format)}
              download
              data-testid={`disa-aktar-${dataset}-${format}`}
              onClick={(e) => {
                /*
                 * Varsayılan indirme ERTELENİR, yoklama sonucu gelince elle
                 * tetiklenir: tavana takılıysa dosya hiç istenmez.
                 */
                e.preventDefault();
                const url = href(format);
                setAcik(false);
                setTavanUyarisi(null);
                void tavanaTakildiMi(url).then((takildi) => {
                  if (takildi) {
                    setTavanUyarisi(
                      'Bugünlük dışa aktarma tavanına ulaşıldı — yarın yeniden deneyebilirsiniz.',
                    );
                    return;
                  }
                  window.location.assign(url);
                });
              }}
              className="block px-3 py-1.5 text-[12px] uppercase text-[#374151] hover:bg-[#f9fafb]"
            >
              {format}
            </a>
          ))}
        </div>
      ) : null}

      {tavanUyarisi !== null ? (
        <p
          data-testid={`disa-aktar-${dataset}-tavan`}
          role="alert"
          className="absolute right-0 top-full z-40 mt-1.5 w-64 rounded-lg border border-[#fde68a] bg-[#fffbeb] px-3 py-2 text-[11px] text-[#92400e]"
        >
          {tavanUyarisi}
        </p>
      ) : null}
    </div>
  );
}
